import React, { memo } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import moment from "moment"
import { getEventColor } from "pagesV2/pro/Calendar/utils"
import { useTheme } from "context/AntdThemeContext"
import {
  getDefaultCalendarColor,
  getPaymentAccentColor,
  getPrimaryCalendarTextColor,
  resolveBookingColors
} from "pagesV2/pro/Calendar/calendarColorUtils"
import BookingPopover from "pagesV2/pro/Calendar/shared/BookingPopover"
import styles from "./index.module.css"

/** "8AM" — the badge on the bar is tighter than a tile's time, so it drops the space too. */
const formatBadgeTime = (value) => {
  if (!value) return ""
  const parsed = moment(value, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])
  if (!parsed.isValid()) return ""
  return parsed.format(parsed.minute() === 0 ? "hA" : "h:mmA")
}

/**
 * A booking that runs over several days, drawn once as a bar across the columns it covers.
 *
 * The bar is absolutely positioned and widened past its own cell: with `table-layout: fixed`
 * every column is the same width, so N columns is N times this cell's content box plus the
 * gutter (two paddings and two borders) for each boundary it crosses.
 */
const MultiDayBar = ({ booking, span, onOpen }) => {
  const { theme } = useTheme()
  const primaryTextColor = getPrimaryCalendarTextColor(theme)
  const previewColors = resolveBookingColors(booking?.color, getDefaultCalendarColor(), primaryTextColor, getEventColor)
  const accentColor = getPaymentAccentColor(booking?.payment_status) || previewColors.borderColor
  const badge = formatBadgeTime(booking?.start_time)
  const tint = previewColors.backgroundColor

  return (
    <BookingPopover booking={booking} colors={previewColors}>
      <Button
        type="link"
        className={styles.bar}
        style={{ "--span": span, backgroundImage: `linear-gradient(${tint}, ${tint})` }}
        onClick={() => onOpen(booking?.uuid)}
      >
        <span className={styles.accent} style={{ backgroundColor: accentColor }} aria-hidden />
        <span className={styles.content}>
          {badge && <span className={styles.badge}>{badge}</span>}
          <span className={styles.title}>{booking?.client_name || booking?.title}</span>
        </span>
      </Button>
    </BookingPopover>
  )
}

MultiDayBar.propTypes = {
  booking: PropTypes.shape({
    uuid: PropTypes.string,
    title: PropTypes.string,
    client_name: PropTypes.string,
    start_time: PropTypes.string,
    payment_status: PropTypes.string,
    color: PropTypes.string
  }).isRequired,
  /** How many columns the bar covers, counting the one it starts in. */
  span: PropTypes.number.isRequired,
  onOpen: PropTypes.func.isRequired
}

export default memo(MultiDayBar)
